import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuPosition } from 'ui-components/components/INTERNAL/dropdown/dropdown-menu-position';

import { DropdownActionsProvider } from './dropdown-actions-context';
import { PrintLabelAction, ViewOnMarketplaceAction, SendLabelAction, CancelAction } from './dropdown-items';
import { useEntitiesSalesOrder } from '../../../../../hooks/entities/selectors/sales-orders';
import { useEnhancedReturnsContext } from '../../../context/enhanced-returns-context-provider';
import { useSalesOrderIdContext } from '../../../../context/sales-order-id-context-provider';
import { useReturnDataContext } from '../../return-data-provider';
import { isAwaitingShipment, isCompleted } from '../../../../../store/returns/returns.helpers';

import { messages } from './messages';
import type { ActionProps } from './dropdown-actions-types';
import style from './styles.module.less';

type Action = React.FC<ActionProps>;

export const DropdownActions = () => {
  const { requestDetails, reloadRequestDetails } = useReturnDataContext();
  const { loadEnhancedReturns } = useEnhancedReturnsContext();
  const salesOrderId = useSalesOrderIdContext();
  const salesOrder = useEntitiesSalesOrder(salesOrderId) ?? {};

  const storeGuid = salesOrder?.store?.storeGuid;
  const completed = isCompleted(requestDetails);
  const isShipmentNeeded = isAwaitingShipment(requestDetails);

  const actions = useMemo(() => {
    const availableActions: Action[] = [ViewOnMarketplaceAction];
    if (!isShipmentNeeded) {
      availableActions.unshift(PrintLabelAction, SendLabelAction);
    }
    if (!completed) availableActions.push(CancelAction);
    return availableActions;
  }, [isShipmentNeeded, completed]);

  const onReload = useCallback(() => {
    reloadRequestDetails();
    loadEnhancedReturns();
  }, [reloadRequestDetails, loadEnhancedReturns]);

  return (
    <DropdownActionsProvider>
      <DropdownMenu dropdownMenuClassName={style.menu} menuPosition={DropdownMenuPosition.BottomRight}>
        <DropdownMenuToggler chevronClassname={style.chevron} className={style.toggler} theme='tertiary'>
          <FormattedMessage {...messages.returnActionsText} />
        </DropdownMenuToggler>
        {actions.map((Action, index) => (
          <Action key={index} data={requestDetails} onReload={onReload} storeGuid={storeGuid} />
        ))}
      </DropdownMenu>
    </DropdownActionsProvider>
  );
};
